import { useState } from "react";
import { Form, notification, UploadFile } from "antd";
import { CheckSquareFilled } from "@ant-design/icons";

import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import { SingleFileDragger } from "@/ui-components/commonComponents/SingleFileDragger/SingleFileDragger";

import {
  NormsHttpService,
  NormUploadResult,
  NormUploadSuccessResponse,
} from "@/shared/services/norms/norms-http.service";

import style from "./style.module.css";
import { BasicModal } from "@/ui-components/commonComponents/Modal/BasicModal";
import { ValidationErrorsModal } from "@/shared/components/ValidationErrorsModal/ValidationErrorsModal";
import { SEO } from "@/ui-components/commonComponents/SEO/SEO";

function SuccessModalContent(uploadResult: NormUploadSuccessResponse) {
  return (
    <div>
      <h2 className={style.modalTitle}>Norm(s) Created Successfully</h2>
      <p className={style.successMessage}>
        <CheckSquareFilled className={style.successCheck} />
        Created {uploadResult.totalRecords} norm record(s) across {uploadResult.created.length} test(s):
      </p>
      <ul className={style.summaryList}>
        {uploadResult.created.map((created) => (
          <li key={created.testName}>
            {created.testName} - {created.normName} ({created.scaleCount})
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function NewNorms() {
  const [form] = Form.useForm<{ file?: UploadFile }>();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<NormUploadResult>();

  const clearFormState = () => {
    form.resetFields();
    setUploadResult(undefined);
  };

  const handleFinish = async (values: { file?: UploadFile }) => {
    const file = values.file?.originFileObj;
    if (!file) {
      notification.error({ message: "Invalid form. Please refresh the page and try again." });
      return;
    }

    try {
      setIsUploading(true);
      setUploadResult(undefined);

      const data = await NormsHttpService.uploadNorms(file);
      setUploadResult(data);
    } catch {
      notification.error({ message: "An unexpected error occurred while uploading the norms. Please try again." });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="main-container">
      <SEO titleParams={["Norms", "New Norms"]} />

      <div className="inner-container">
        <h1 className={style.pageTitle}>New Norms</h1>
        <div className={style.formContainer}>
          <div className={style.instructionsContainer}>
            <p> Only one file can be uploaded at a time. Multiple norms can be added into an individual file.</p>
          </div>
          <Form form={form} className={style.form} onFinish={handleFinish}>
            <Form.Item
              name="file"
              valuePropName="file"
              rules={[{ required: true, message: "Please choose a CSV file before uploading." }]}
            >
              <SingleFileDragger accept=".csv,text/csv" hintText="Support for a single CSV file upload" />
            </Form.Item>

            <SubmitButton arrowAfter={true} isLoading={isUploading}>
              Submit
            </SubmitButton>
          </Form>
        </div>
      </div>

      {uploadResult &&
        (uploadResult.success ? (
          <BasicModal isModalOpen={Boolean(uploadResult)} handleCancel={clearFormState}>
            <SuccessModalContent {...uploadResult} />
          </BasicModal>
        ) : (
          <ValidationErrorsModal
            title="Norm(s) Creation Failed"
            errors={uploadResult.errors}
            onClose={clearFormState}
          />
        ))}
    </div>
  );
}
